import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { TwoFactorDisableModal } from "@/pages/me/widgets/TwoFactorDisableModal.tsx";
import { TwoFactorSetupModal } from "@/pages/me/widgets/TwoFactorSetupModal.tsx";
import { ChangePasswordModal } from "@/pages/security/widgets/ChangePasswordModal.tsx";
import {
  useEmployeeMe,
  useEmployeeResetConfirmEmail,
  useEmployeeUpdateAccountSettings,
} from "@/queries/employees/use-employees.query.ts";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiEmployeeAccountSettingsSavePayload,
  BirthdayDisplayType,
} from "@/types/employees/employee.types.ts";
import { BirthdayDisplayFormats } from "@/types/organization.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import {
  ButtonIcon,
  FontAwesomeIcon,
  LinkButtonTo,
  ListItem,
  Page,
  Select,
  Stack,
  Switch,
  Text,
  Title,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export const AccountSettingsSecurity = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: employee } = useEmployeeMe();
  const { data: appConfig } = useAppConfig();
  const { canEditField } = usePermissions();
  const resetConfirmEmail = useEmployeeResetConfirmEmail();
  const updateAccountSettings = useEmployeeUpdateAccountSettings();
  const modalsService = useModals();
  const { showInfo } = useToasts();
  const { control, reset } = useForm<{
    birthday_display?: BirthdayDisplayType | null;
    show_full_salary?: boolean;
  }>();

  useEffect(() => {
    reset({
      birthday_display: employee?.birthday_display,
      show_full_salary: appConfig?.ui.settings.show_full_salary,
    });
  }, [appConfig?.ui.settings.show_full_salary, employee?.birthday_display, reset]);

  const birthdayDisplayOptions = useMemo(() => {
    const limit = employee?.organization?.birthday_display;
    if (!limit) return [];
    return BirthdayDisplayFormats.filter((item) => {
      if (limit === BirthdayDisplayType.full) return true;
      if (limit === BirthdayDisplayType.short) return item.id !== BirthdayDisplayType.full;
      return item.id === BirthdayDisplayType.none;
    }).map((item) => ({ id: item.id, title: t(item.title) }));
  }, [employee?.organization?.birthday_display, t]);

  const canEditBirthdayDisplay =
    employee?.birthday_display != null &&
    canEditField(employee, EmployeeFieldPermission.employee_date_birth_display);
  const canConfigureFullSalaryVisibility =
    appConfig?.ui.settings.can_configure_full_salary_visibility ?? false;
  const hasPrivacySettings = canEditBirthdayDisplay || canConfigureFullSalaryVisibility;

  const savePrivacySettings = (payload: ApiEmployeeAccountSettingsSavePayload) => {
    updateAccountSettings.mutate(payload, {
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.employees.accountSettingsEdited);
        showInfo({ title: t("common:changes_saved_successfully") });
      },
    });
  };

  useTitle(t("common:security"));

  const handleResendVerificationEmail = () => {
    resetConfirmEmail.mutate(undefined, {
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.pageAction, {
          [analyticProperties.actionType]: "Email Confirmation Sent",
        });
        showInfo({ title: t("core:confirmation_email_sent", { email: employee?.email }) });
      },
    });
  };

  return (
    <Page>
      <Page.Header
        backSlot={
          <ButtonIcon
            variant={"secondary"}
            icon={<FontAwesomeIcon icon={"fa-light fa-arrow-left"} />}
            onClick={() => navigate("/account-settings")}
          />
        }
        title={t("common:security")}
      />
      <Page.Content>
        <Stack className={"container-sm mx-auto"} gap={"sm"}>
          <Stack className={"card-panel-sm"} gap={"2xl"}>
            <Title header={t("core:account_general_security")} />
            <Stack>
              <ListItem
                preTitle={t("core:account_email")}
                title={employee?.email}
                captionSlot={
                  employee &&
                  !employee.email_confirmed && (
                    <Text className={"text-warning"}>{t("core:email_not_confirmed")}</Text>
                  )
                }
                valueSlot={
                  employee &&
                  !employee.email_confirmed && (
                    <LinkButtonTo
                      onClick={handleResendVerificationEmail}
                      isLoading={resetConfirmEmail.isPending}
                    >
                      {t("core:resend_verification_email")}
                    </LinkButtonTo>
                  )
                }
              />
              <ListItem
                preTitle={t("common:password")}
                title={"**********"}
                valueSlot={
                  <LinkButtonTo onClick={() => modalsService.openModal(ChangePasswordModal, {})}>
                    {t("common:change_password")}
                  </LinkButtonTo>
                }
              />
              <ListItem
                className={"border-0"}
                preTitle={t("core:two_factor")}
                titleSlot={
                  <Text
                    className={twMerge(
                      "font-semibold text-alert",
                      employee?.tfa_enabled && "text-positive"
                    )}
                  >
                    {employee?.tfa_enabled ? t("common:enabled") : t("common:disabled")}
                  </Text>
                }
                valueSlot={
                  <LinkButtonTo
                    onClick={() =>
                      modalsService.openModal(
                        employee?.tfa_enabled ? TwoFactorDisableModal : TwoFactorSetupModal
                      )
                    }
                  >
                    {employee?.tfa_enabled ? t("common:disable") : t("common:enable")}
                  </LinkButtonTo>
                }
              />
            </Stack>
          </Stack>
          {hasPrivacySettings && (
            <Stack className={"card-panel-sm"} gap={"2xl"}>
              <Title header={t("common:privacy")} />
              <Stack gap={"md"}>
                {canEditBirthdayDisplay && (
                  <Controller
                    control={control}
                    name="birthday_display"
                    render={({ field: { value, onChange } }) => (
                      <Select
                        value={value}
                        onChange={(nextValue) => {
                          const birthdayDisplay = nextValue as BirthdayDisplayType | null;
                          onChange(birthdayDisplay);
                          if (birthdayDisplay) {
                            savePrivacySettings({ birthday_display: birthdayDisplay });
                          }
                        }}
                        label={t("core:birthday_display")}
                        options={birthdayDisplayOptions}
                        emptyTitle={t("common:select")}
                        isLoading={updateAccountSettings.isPending}
                      />
                    )}
                  />
                )}
                {canConfigureFullSalaryVisibility && (
                  <Controller
                    control={control}
                    name="show_full_salary"
                    render={({ field: { value, onChange } }) => (
                      <Switch
                        size="sm"
                        checked={!!value}
                        onChange={(nextValue) => {
                          onChange(nextValue);
                          savePrivacySettings({ show_full_salary: nextValue });
                        }}
                        disabled={updateAccountSettings.isPending}
                        title={t("core:show_full_salary")}
                        description={t("core:show_full_salary_description")}
                      />
                    )}
                  />
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Page.Content>
    </Page>
  );
};
